import React from 'react';
import EssayComposer from './composer/EssayComposer';
import ActivityComposer from './composer/ActivityComposer';
import { MCQComposer } from './composer/MCQComposer';
import { Task, TaskScope } from './types';
import { useSelectedTask } from '../../common/hooks/student/useSelectedTask';
import { useAuthStore } from '../../common/auth/store';
import { useGetUserBookings } from '../../common/api/endpoints/appointments/appointments';

type Props = {
  task: Task;
  readonly?: boolean;
  testID?: string;
  isMobileView?: boolean;
  isLoading?: boolean;
  taskScope: TaskScope;
  safetyFuseConflict?: boolean;
  onOpeningPageChange?: (isShowing: boolean) => void;
};

const ResponseComposer: React.FC<Props> = ({
  task,
  readonly = false,
  testID = 'response-composer',
  isMobileView = false,
  isLoading = false,
  taskScope,
  safetyFuseConflict = false,
  onOpeningPageChange,
}) => {
  const selection = useSelectedTask?.();
  const derivedIsLoading = isLoading || selection?.isTasksLoading;
  const type = task?.type;
  const user = useAuthStore((state) => state.user);
  const isEventDeliveredMcq = type === 'multiple_choice' && Boolean(task?.eventConfig?.eventKindId);

  const { data: userBookings } = useGetUserBookings(user?.id || '', undefined, {
    query: {
      enabled: Boolean(user?.id) && isEventDeliveredMcq,
      select: (resp) => resp?.data?.data || [],
    },
  });

  const slotId = isEventDeliveredMcq
    ? userBookings?.find(
      (booking) => booking.taskId === task.id
        && booking.moduleId === taskScope.moduleId
        && booking.courseId === taskScope.courseId
        && booking.status !== 'cancelled',
    )?.slotId
    : undefined;
  const isTrialStudent = Boolean(user?.trial?.isTrial);
  const trialHeldEventBooking = isTrialStudent && isEventDeliveredMcq && !!slotId;
  const mcqReadOnly = readonly && !trialHeldEventBooking;

  if (type === 'activity') {
    return (
      <ActivityComposer task={task} readonly={readonly} testID={testID} isLoading={derivedIsLoading} taskScope={taskScope} />
    );
  }

  if (type === 'multiple_choice') {
    return (
      <MCQComposer
        courseId={taskScope.courseId}
        moduleId={taskScope.moduleId}
        taskId={task.id}
        version={task.version}
        slotId={slotId}
        readOnly={mcqReadOnly}
        submissionMcqResult={task.submission?.mcqResult}
        showImmediateFeedback={task.mcqConfig?.showImmediateFeedback}
      />
    );
  }

  if (type === 'essay') {
    return (
      <EssayComposer
        task={task}
        readonly={readonly}
        testID={testID}
        isMobileView={isMobileView}
        isLoading={derivedIsLoading}
        taskScope={taskScope}
        safetyFuseConflict={safetyFuseConflict}
        onOpeningPageChange={onOpeningPageChange}
      />
    );
  }

  return null;
};

export default ResponseComposer;
